import { useCallback, useEffect, useRef, useState } from 'react';
import { getCell } from '../domain/pattern';
import { EMPTY } from '../domain/types';
import type { Cell } from '../domain/types';
import {
  brushAt,
  collectOps,
  ellipse,
  floodFill,
  line,
  rectangle,
  withSymmetry,
} from '../editor/draw';
import type { Op } from '../editor/draw';
import { swapColor } from '../domain/pattern';
import { DARK_THEME, LIGHT_THEME, cellAtScreen, fitView, renderChart } from '../render/chart';
import { useEditor } from './kit';

type DragMode = 'none' | 'paint' | 'shape' | 'pan' | 'select' | 'move' | 'gesture';

/** State captured when a two-finger pinch begins. */
interface PinchStart {
  distance: number;
  midX: number;
  midY: number;
  zoom: number;
  panX: number;
  panY: number;
}

function inGrid(p: { width: number; height: number }, c: Cell): boolean {
  return c.col >= 0 && c.row >= 0 && c.col < p.width && c.row < p.height;
}

export function CanvasView({ theme }: { theme: 'dark' | 'light' }) {
  const { store, state } = useEditor();
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [hover, setHover] = useState<Cell | null>(null);
  const [refImage, setRefImage] = useState<HTMLImageElement | null>(null);

  const drag = useRef<{
    mode: DragMode;
    anchor: Cell | null;
    last: Cell | null;
    value: number;
    startX: number;
    startY: number;
    panX: number;
    panY: number;
    floatOffset: { dc: number; dr: number };
  }>({
    mode: 'none',
    anchor: null,
    last: null,
    value: EMPTY,
    startX: 0,
    startY: 0,
    panX: 0,
    panY: 0,
    floatOffset: { dc: 0, dr: 0 },
  });

  // Every finger currently down, so a second one can turn a stroke into a pinch.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<PinchStart | null>(null);

  // Track the element size so the canvas always matches its box.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setSize({ w: Math.max(120, r.width), h: Math.max(120, r.height) });
    });
    ro.observe(el);
    const r = el.getBoundingClientRect();
    setSize({ w: Math.max(120, r.width), h: Math.max(120, r.height) });
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const src = state.reference?.src;
    if (!src) {
      setRefImage(null);
      return;
    }
    const img = new Image();
    img.onload = () => setRefImage(img);
    img.src = src;
  }, [state.reference?.src]);

  // Draw.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(size.w * dpr);
    canvas.height = Math.round(size.h * dpr);
    canvas.style.width = `${size.w}px`;
    canvas.style.height = `${size.h}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    renderChart(ctx, {
      pattern: state.pattern,
      view: state.view,
      preview: state.preview,
      selection: state.selection,
      floating: state.floating,
      hover,
      reference:
        refImage && state.reference?.visible
          ? {
              image: refImage,
              opacity: state.reference.opacity,
              x: state.reference.x,
              y: state.reference.y,
              scale: state.reference.scale,
            }
          : null,
      width: size.w,
      height: size.h,
      theme: theme === 'dark' ? DARK_THEME : LIGHT_THEME,
    });
  }, [state.rev, state.view, state.selection, state.floating, state.preview, state.pattern, size, hover, refImage, state.reference, theme]);

  const fit = useCallback(() => {
    store.setView(fitView(store.pattern, size.w, size.h, store.getSnapshot().view.showRulers));
  }, [store, size.w, size.h]);

  // Fit the view when a different pattern is loaded.
  const lastPatternId = useRef<string>('');
  useEffect(() => {
    if (state.pattern.id !== lastPatternId.current) {
      lastPatternId.current = state.pattern.id;
      fit();
    }
  }, [state.pattern.id, fit]);

  // Re-fit after a big change in canvas size — rotating a tablet, or dragging a
  // window across a large range. Small changes leave the view alone so an
  // in-progress zoom is not thrown away.
  const lastFitSize = useRef({ w: 0, h: 0 });
  useEffect(() => {
    const prev = lastFitSize.current;
    const changedALot =
      prev.w > 0 &&
      (Math.abs(size.w - prev.w) / prev.w > 0.25 || Math.abs(size.h - prev.h) / prev.h > 0.25);
    if (prev.w === 0 || changedALot) {
      lastFitSize.current = { w: size.w, h: size.h };
      if (changedALot) fit();
    }
  }, [size.w, size.h, fit]);

  // Wheel zoom needs a non-passive listener to stop the page scrolling.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const view = store.getSnapshot().view;
      const rect = el.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      if (e.shiftKey && !e.ctrlKey && !e.metaKey) {
        store.setView({ panX: view.panX - e.deltaY, panY: view.panY });
        return;
      }
      const factor = Math.exp(-e.deltaY * 0.0016);
      const zoom = Math.max(1, Math.min(80, view.zoom * factor));
      // Keep the point under the cursor pinned while zooming.
      const k = zoom / view.zoom;
      store.setView({
        zoom,
        panX: px - (px - view.panX) * k,
        panY: py - (py - view.panY) * k,
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [store]);

  const cellFromEvent = useCallback(
    (e: { clientX: number; clientY: number }, allowOutside = false): Cell | null => {
      const el = canvasRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const s = store.getSnapshot();
      return cellAtScreen(s.pattern, s.view, e.clientX - rect.left, e.clientY - rect.top, allowOutside);
    },
    [store],
  );

  const paintOps = useCallback(
    (from: Cell, to: Cell, value: number): Op[] => {
      const s = store.getSnapshot();
      return collectOps(s.pattern, value, (plot) => {
        const sym = withSymmetry(s.pattern, { h: s.symH, v: s.symV }, plot);
        line(from, to, (c, r) => brushAt(c, r, s.brushSize, sym));
      });
    },
    [store],
  );

  const shapeOps = useCallback(
    (a: Cell, b: Cell, value: number): Op[] => {
      const s = store.getSnapshot();
      return collectOps(s.pattern, value, (plot) => {
        const sym = withSymmetry(s.pattern, { h: s.symH, v: s.symV }, plot);
        if (s.tool === 'line') line(a, b, (c, r) => brushAt(c, r, s.brushSize, sym));
        else if (s.tool === 'rect') rectangle(a, b, s.shapeFilled, sym);
        else if (s.tool === 'ellipse') ellipse(a, b, s.shapeFilled, sym);
      });
    },
    [store],
  );

  const localPoint = (e: { clientX: number; clientY: number }) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    return { x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) };
  };

  /** Abandon whatever the first finger was doing so a pinch can take over. */
  const startPinch = () => {
    const d = drag.current;
    if (d.mode === 'paint') store.cancelStroke();
    else if (d.mode === 'shape') store.setPreview([]);
    else if (d.mode === 'move') store.stampFloating();
    d.mode = 'gesture';
    d.anchor = null;
    d.last = null;
    setHover(null);

    const pts = [...pointers.current.values()];
    if (pts.length < 2) return;
    const s = store.getSnapshot();
    pinch.current = {
      distance: Math.max(1, Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y)),
      midX: (pts[0].x + pts[1].x) / 2,
      midY: (pts[0].y + pts[1].y) / 2,
      zoom: s.view.zoom,
      panX: s.view.panX,
      panY: s.view.panY,
    };
  };

  const updatePinch = () => {
    const start = pinch.current;
    const pts = [...pointers.current.values()];
    if (!start || pts.length < 2) return;
    const distance = Math.max(1, Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y));
    const midX = (pts[0].x + pts[1].x) / 2;
    const midY = (pts[0].y + pts[1].y) / 2;
    const zoom = Math.max(1, Math.min(80, (start.zoom * distance) / start.distance));
    const k = zoom / start.zoom;
    // Keep the point between the fingers pinned, and let the midpoint drift
    // carry the chart along so pinch and pan are one gesture.
    store.setView({
      zoom,
      panX: midX - (start.midX - start.panX) * k,
      panY: midY - (start.midY - start.panY) * k,
    });
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    pointers.current.set(e.pointerId, localPoint(e));
    if (pointers.current.size >= 2) {
      startPinch();
      return;
    }
    try {
      // Keeps a drag alive when the cursor leaves the canvas. Not every pointer
      // can be captured, and failing to is never a reason to drop the stroke.
      (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const s = store.getSnapshot();
    const cell = cellFromEvent(e);
    const d = drag.current;
    d.startX = e.clientX;
    d.startY = e.clientY;
    d.panX = s.view.panX;
    d.panY = s.view.panY;

    const wantsPan = s.tool === 'pan' || e.button === 1 || (e.button === 0 && e.altKey);
    if (wantsPan) {
      d.mode = 'pan';
      return;
    }
    if (!cell) return;

    // Right button paints with the secondary colour, which defaults to empty.
    const value = e.button === 2 ? s.secondary : s.primary;

    switch (s.tool) {
      case 'pencil':
      case 'eraser': {
        d.mode = 'paint';
        d.value = s.tool === 'eraser' ? EMPTY : value;
        d.last = cell;
        store.beginStroke();
        store.applyOps(paintOps(cell, cell, d.value));
        break;
      }
      case 'line':
      case 'rect':
      case 'ellipse': {
        d.mode = 'shape';
        d.anchor = cell;
        d.value = value;
        store.setPreview(shapeOps(cell, cell, value));
        break;
      }
      case 'fill': {
        const ops = collectOps(s.pattern, value, (plot) => {
          floodFill(s.pattern, cell, withSymmetry(s.pattern, { h: s.symH, v: s.symV }, plot));
        });
        store.beginStroke();
        store.applyOps(ops);
        store.endStroke();
        break;
      }
      case 'replace': {
        const target = getCell(s.pattern, cell.col, cell.row);
        if (target !== value) {
          store.commit((p) => swapColor(p, target, value), 'Replaced colour');
        }
        break;
      }
      case 'picker': {
        const v = getCell(s.pattern, cell.col, cell.row);
        store.set({ primary: v, tool: 'pencil' });
        store.status(v === EMPTY ? 'Picked: empty' : `Picked ${s.pattern.palette[v]?.name ?? ''}`);
        break;
      }
      case 'select': {
        const sel = s.selection;
        const inside =
          sel && cell.col >= sel.x && cell.col < sel.x + sel.w && cell.row >= sel.y && cell.row < sel.y + sel.h;
        if (inside && sel) {
          if (!s.floating) store.liftSelection();
          d.mode = 'move';
          d.floatOffset = { dc: cell.col - sel.x, dr: cell.row - sel.y };
        } else {
          if (s.floating) store.stampFloating();
          d.mode = 'select';
          d.anchor = cell;
          store.setSelection({ x: cell.col, y: cell.row, w: 1, h: 1 });
        }
        break;
      }
      default:
        break;
    }
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const d = drag.current;
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, localPoint(e));
    if (d.mode === 'gesture') {
      updatePinch();
      return;
    }
    if (d.mode === 'none') {
      setHover(cellFromEvent(e));
      return;
    }
    // Mid-drag the pointer is allowed to leave the grid: ops are clipped when
    // they are applied, so the stroke resumes correctly on the way back in.
    const cell = cellFromEvent(e, true);
    const s = store.getSnapshot();

    if (d.mode === 'pan') {
      store.setView({ panX: d.panX + (e.clientX - d.startX), panY: d.panY + (e.clientY - d.startY) });
      return;
    }
    if (!cell) return;

    if (d.mode === 'paint' && d.last) {
      store.applyOps(paintOps(d.last, cell, d.value));
      d.last = cell;
      setHover(inGrid(s.pattern, cell) ? cell : null);
      return;
    }
    if (d.mode === 'shape' && d.anchor) {
      store.setPreview(shapeOps(d.anchor, cell, d.value));
      setHover(inGrid(s.pattern, cell) ? cell : null);
      return;
    }
    if (d.mode === 'select' && d.anchor) {
      const cx = Math.max(0, Math.min(s.pattern.width - 1, cell.col));
      const cy = Math.max(0, Math.min(s.pattern.height - 1, cell.row));
      const x = Math.min(d.anchor.col, cx);
      const y = Math.min(d.anchor.row, cy);
      store.setSelection({
        x,
        y,
        w: Math.abs(cx - d.anchor.col) + 1,
        h: Math.abs(cy - d.anchor.row) + 1,
      });
      return;
    }
    if (d.mode === 'move' && s.floating) {
      store.moveFloating(cell.col - d.floatOffset.dc, cell.row - d.floatOffset.dr);
    }
  };

  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    pointers.current.delete(e.pointerId);
    const d = drag.current;
    const s = store.getSnapshot();

    if (d.mode === 'gesture') {
      if (pointers.current.size >= 2) {
        // A third finger lifted — re-anchor the pinch to the two that remain.
        startPinch();
      } else if (pointers.current.size === 0) {
        d.mode = 'none';
        pinch.current = null;
      }
      // With exactly one finger left, stay in gesture mode rather than
      // suddenly drawing a line from wherever that finger happens to be.
      return;
    }

    if (d.mode === 'paint') {
      store.endStroke();
    } else if (d.mode === 'shape' && d.anchor) {
      const ops = s.preview;
      store.setPreview([]);
      if (ops.length) {
        store.beginStroke();
        store.applyOps(ops);
        store.endStroke();
      }
    }
    d.mode = 'none';
    d.anchor = null;
    d.last = null;
  };

  const cursor =
    state.tool === 'pan'
      ? 'grab'
      : state.tool === 'picker'
        ? 'crosshair'
        : state.tool === 'select'
          ? 'default'
          : 'crosshair';

  return (
    <div className="canvas-wrap" ref={wrapRef}>
      <canvas
        ref={canvasRef}
        style={{ cursor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => setHover(null)}
        onContextMenu={(e) => e.preventDefault()}
      />
      <div className="canvas-hud">
        <button className="hud-btn" onClick={fit} title="Fit pattern to window (0)">
          Fit
        </button>
        <button
          className="hud-btn"
          onClick={() => store.setView({ zoom: Math.min(80, state.view.zoom * 1.25) })}
          title="Zoom in (+)"
        >
          +
        </button>
        <button
          className="hud-btn"
          onClick={() => store.setView({ zoom: Math.max(1, state.view.zoom / 1.25) })}
          title="Zoom out (−)"
        >
          −
        </button>
        <span className="hud-read">
          {hover ? `col ${hover.col + 1}, row ${state.pattern.height - hover.row}` : `${Math.round(state.view.zoom * 10) / 10} px/mm`}
        </span>
      </div>
    </div>
  );
}
